"use client";

// WO-GF-001-S20 · F131 — the three post-results funnel screens, built to
// BOOKING-FLOW-CONTRACT-GF-001.md §4 (DETAILS) and its ADDENDUM (STORE, REVIEW & PAY).
//
// The addendum exists because the first walk could not reach past DETAILS without submitting passenger
// data; the founder then authorised dummy details, which revealed that MARKETPLACE is BOTH a stepper
// label that links out AND a real in-funnel screen at `/booking/store`. Both are built here.

import { useEffect, useMemo, useState } from "react";
import { isSignedIn, useCartStore } from "@/lib/cart-store";
import { emitPurchaseOnce } from "@/lib/analytics-purchase";
import type { CartItem } from "@/lib/cart-store";
import BookingStepper, { FunnelChrome, FUNNEL, NAVY, ORANGE, GREY, RULE } from "./BookingChrome";
// FWLR-025-R7 · J — the legs the founder's design holds (sg-cart-v1) are the legs this journey books; read beside the host cart.
import { useDesignCartLines, journeyLabel, designCartCheckoutHref } from "@/components/marketplace/DesignCartLines";
import type { DesignCartLine } from "@/components/marketplace/DesignCartLines";

// §25.6 founder ruling — prices render rounded, no decimal point. Rounded (never truncated): a
// truncated price reads LOWER than what is charged. See lib/cart-billing.ts `money` for the full note.
const money = (n: number) => `$${Math.round(n)}`;

const isDepart = (i: CartItem) => /^BUS SEAT \(Departure\)$/.test(i.title);

/** The shapes `/api/legacy/booking/submit` (route.ts) actually returns — booked, staged, or refused. */
interface LegacyBookingSubmitResponse {
  booked?: boolean;
  orderId?: string;
  staged?: boolean;
  reason?: string;
  error?: string;
  upstreamStatus?: number;
}

/**
 * FWLR-025-R7 · J — the design's transport legs in the host cart's shape, so DETAILS and REVIEW show the departure and
 * return the customer actually picked (the results leg commits into sg-cart-v1, not into the host store). A design
 * leg already mirrored into the host store (same id) is not listed twice. Prices are the line's recorded price — what
 * the design charged from live's board — never re-derived here.
 */
export function mergeDesignLegs(hostItems: CartItem[], design: DesignCartLine[] | null, mountainCode: string): CartItem[] {
  const host = hostItems.filter((i) => (i.section ?? "marketplace") === "transport");
  const seen = new Set(host.map((i) => i.id));
  const extra: CartItem[] = (design ?? [])
    .filter((l) => l.cat === "Transport" && !seen.has(l.id))
    .map((l) => ({
      id: l.id,
      key: l.id,
      listingId: null,
      title: l.legKind === "RET" ? "BUS SEAT (Return)" : "BUS SEAT (Departure)",
      guestPrice: l.price,
      memberPrice: l.price,
      qty: l.qty,
      startDate: l.date ?? "",
      endDate: l.date ?? "",
      mountain: mountainCode,
      supplier: l.vendor ?? "",
      supplierSlug: "",
      booked: false,
      orderDocumentId: null,
      section: "transport",
      depTime: l.dep,
      depStop: l.depStop,
      arrTime: l.arr,
      arrStop: l.arrStop,
      tripId: l.tripId,
      journey: l.journey === "round-trip" ? "round-trip" : l.journey === "one-way" ? "one-way" : undefined,
      // SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — the engine's own schedule id and the SearchCriteriaDTO this
      // leg was searched under (§18b), carried from `commitRide` so `resolvePayResult` can build a real
      // legacy booking submission from a design-held leg exactly as it already does for a host-store one
      // (BookingResults.tsx). Honest refusal: absent on the design line ⇒ absent here, never guessed.
      ...(l.sgSid !== undefined && l.sgSid !== null && l.sgSid !== "" ? { itemIdentifier: l.sgSid } : {}),
      ...(l.departureCode && l.arrivalCode && l.travelDate
        ? {
            searchCriteria: {
              departureCode: l.departureCode,
              arrivalCode: l.arrivalCode,
              travelDate: l.travelDate,
              ...(l.returnDate ? { returnDate: l.returnDate } : {}),
              ...(l.adult !== undefined ? { adult: l.adult } : {}),
              ...(l.child !== undefined ? { child: l.child } : {}),
              ...(l.senior !== undefined ? { senior: l.senior } : {}),
              ...(l.student !== undefined ? { student: l.student } : {}),
              ...(l.noStore !== undefined ? { noStore: l.noStore } : {}),
              ...(l.returnTrip !== undefined ? { returnTrip: l.returnTrip } : {}),
            },
          }
        : {}),
    }));
  return [...host, ...extra];
}

/**
 * S24 · F156/F159 (W7/W10) — the shared pre-fill hook for the two contact forms. Loads the signed-in
 * user's profile (loadProfile → /api/me) and fills empty fields from it, WITHOUT clobbering anything the
 * user typed (`cur.x || …`). The two forms keep their own state (Passenger vs Customer — the distinction is
 * preserved on purpose: DETAILS collects the TRAVELLER/primary contact, REVIEW the CUSTOMER/payer who
 * receives the ticket via email), so each simply calls this hook with its own setter.
 */
type ContactForm = { firstName: string; lastName: string; email: string; emailConfirm: string; phoneExt: string; phone: string };
function usePrefill(setForm: React.Dispatch<React.SetStateAction<ContactForm>>) {
  const session = useCartStore((s) => s.session);
  const loadProfile = useCartStore((s) => s.loadProfile);
  // A bearer session loads its profile through /api/me; a cookie-backed SSO session already carries the verified profile fields
  // (name, phone) from the server-verified /api/auth/session read, so there is nothing to fetch for it — and the pre-fill below
  // works for both.
  const signed = isSignedIn(session);
  useEffect(() => {
    if (session.jwt) void loadProfile();
  }, [session.jwt, loadProfile]);
  useEffect(() => {
    if (!signed) return;
    setForm((cur) => ({
      ...cur,
      firstName: cur.firstName || session.firstName || "",
      lastName: cur.lastName || session.lastName || "",
      email: cur.email || session.email || "",
      emailConfirm: cur.emailConfirm || session.email || "",
      phone: cur.phone || session.phone || "",
    }));
  }, [signed, session.firstName, session.lastName, session.email, session.phone, setForm]);
}

/* ─────────────────────────────── SCREEN 4 · DETAILS ─────────────────────────────── */

/**
 * Contract §4. Two things live does that a reasonable guess would get wrong, both verified in the walk:
 *  • only **Traveller 1** is ever collected. Walked with `adult=2&child=1` and live STILL rendered one
 *    traveller — the `p1.` field prefix implies p2/p3 but live never renders them. So no traveller 2..n
 *    forms are built here, and the optional free-text field carries the rest, exactly as live does.
 *  • `Continue` is GATED on the required fields (live does not navigate with an empty form).
 */
export function BookingDetailsScreen({
  mountainCode,
  mountainName,
  onwardHref,
}: {
  mountainCode: string;
  mountainName: string;
  onwardHref: string;
}) {
  const items = useCartStore((s) => s.items);
  const designLines = useDesignCartLines();
  const legs = useMemo(() => mergeDesignLegs(items, designLines, mountainCode), [items, designLines, mountainCode]);
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", emailConfirm: "", phoneExt: "+1", phone: "" });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((s) => ({ ...s, [k]: e.target.value }));
  // S24 · F156/W7 — a signed-in member's PASSENGER info pre-fills (name / email / phone), without
  // clobbering anything the user has already typed. loadProfile() lazily pulls name+phone from /api/me.
  usePrefill(setF);

  const ready =
    !!f.firstName.trim() && !!f.lastName.trim() && !!f.email.trim() &&
    f.email.trim() === f.emailConfirm.trim() && !!f.phone.trim();

  return (
    <div data-booking-screen="details">
      <FunnelChrome mountainCode={mountainCode} mountainName={mountainName} />
      <BookingStepper active="details" mountainCode={mountainCode} />
      {/* the export's funnel column is `calc(100% - 338px)` beside its rail; these screens carry no
          rail, so they hold the same measure instead of sprawling the full 1140. */}
      <div style={{ ...wrap, maxWidth: 800 }}>
        {/* DoD 10 — the export gives every funnel screen a title + one honest line under it. */}
        <h1 style={FUNNEL.h1}>Who is travelling?</h1>
        <p style={FUNNEL.lede}>
          Only the primary contact is collected — the operator needs one name and one way to reach the
          booking. Nothing is charged at this step.
        </p>

        {/* live's two quick-add chips sit above the form, top-right */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginBottom: 12 }}>
          {["Luggage", "Protect"].map((c) => (
            <div key={c} data-booking-chip={c.toLowerCase()} style={chip}>
              {c}
              <span aria-hidden style={chipPlus}>+</span>
            </div>
          ))}
        </div>

        <section data-booking-panel="passenger" style={panel}>
          <header style={panelHead}>Passenger Information</header>
          <div style={{ padding: 18 }}>
            <div style={{ ...FUNNEL.panelHead, margin: "0 0 12px", color: "var(--sg-grey-600)" }}>
              Traveller 1 : Adult (Primary Contact)
            </div>
            <div style={grid2}>
              <input name="p1.firstName" placeholder="First Name*" value={f.firstName} onChange={set("firstName")} style={input} />
              <input name="p1.lastName" placeholder="Last Name*" value={f.lastName} onChange={set("lastName")} style={input} />
              <input name="p1.email" placeholder="Email*" value={f.email} onChange={set("email")} style={input} />
              <input name="p1.emailConfirm" placeholder="Email Confirmation*" value={f.emailConfirm} onChange={set("emailConfirm")} style={input} />
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <input name="p1.phoneExt" value={f.phoneExt} onChange={set("phoneExt")} style={{ ...input, width: 70, flex: "0 0 70px" }} />
              <input name="p1.phone" placeholder="Phone Number*" value={f.phone} onChange={set("phone")} style={{ ...input, flex: 1 }} />
            </div>
            {/* live's one concession to extra passengers — a single optional free-text field */}
            <input name="p1.additional" placeholder="Additional Travellers info 1 (optional)" style={{ ...input, marginTop: 12, width: "50%" }} />
          </div>
        </section>

        {/* S23 · F146 — Transport Extras, its three tabs now CARRYING CONTENT sourced from the Figma
            booking-extras node (1811:161181) — copy verbatim, no invention. */}
        <TransportExtrasPanel />

        {/* S21 · F137 — live's DETAILS screen carries a "Ticket details" panel summarising both legs
            (captured 2026-07-24). Read from the cart's transport lines: leg label, date, the board
            time-over-stop, ticket count and price. Times/stops are what the results screen stamped on the
            line; the arrival pair is not modelled in the schedule payload so it is omitted, not invented. */}
        <TicketDetailsPanel legs={legs} />

        {/* the export puts its primary action at the end of the column, not centred mid-page */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginTop: 24 }}>
          <a
            data-booking-continue
            data-enabled={ready ? "true" : "false"}
            href={ready ? onwardHref : undefined}
            aria-disabled={!ready}
            onClick={(e) => { if (!ready) e.preventDefault(); }}
            style={{ ...cta, ...(ready ? {} : { background: "var(--sg-grey-300)", cursor: "not-allowed" }) }}
          >
            Continue
          </a>
          <span style={FUNNEL.footnote}>
            Cancel free until the booking is confirmed. Member fares are locked for 30 minutes.
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── SCREEN 5 · BOOKING STORE ─────────────────────────────── */

/** Contract addendum §"SCREEN 5". Skippable — nothing has to be added. */
export function BookingStoreScreen({ mountainCode, mountainName, onwardHref }: { mountainCode: string; mountainName: string; onwardHref: string }) {
  const GROUPS: { title: string; tabs: { label: string; disabled?: boolean }[] }[] = [
    { title: "ACTIVITIES", tabs: [{ label: "SNOWMOBILE" }, { label: "SNOWSHOE" }, { label: "DOGSLED" }] },
    { title: "RENTALS & FOR SALE", tabs: [{ label: "SKI EQUIPMENT" }, { label: "SNOWBOARD EQUIPMENT" }, { label: "FOR SALE NEW/USED", disabled: true }] },
    { title: "LIFT TICKETS & PASSES", tabs: [{ label: "MOUNTAIN LIFT TICKETS", disabled: true }, { label: "MOUNTAIN PASSES", disabled: true }, { label: "TRANSPORT PASSES", disabled: true }] },
  ];
  return (
    <div data-booking-screen="store">
      <FunnelChrome mountainCode={mountainCode} mountainName={mountainName} />
      <BookingStepper active="marketplace" mountainCode={mountainCode} />
      <div style={{ ...wrap, maxWidth: 800 }}>
        <h1 style={FUNNEL.h1}>Anything else for the trip?</h1>
        <p style={FUNNEL.lede}>
          Rentals, activities and passes from the operators who work at {mountainName} — added to the
          same booking as the bus. Skippable: nothing here is required.
        </p>
        <div style={{ ...panel, padding: 18 }}>
          <strong style={{ fontFamily: "var(--sg-font-primary)", fontSize: 15, fontWeight: 800 }}>Welcome to the SNOWCLUB Store!</strong>
          <p style={{ fontFamily: "var(--sg-font-web)", fontSize: 13, color: "var(--sg-grey-600)", margin: "8px 0 0", lineHeight: 1.5 }}>
            Here you will find great deals on all kinds of great stuff from mountain stores.
          </p>
        </div>
        {GROUPS.map((g) => (
          <section key={g.title} data-store-group={g.title} style={{ marginTop: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span aria-hidden style={chipPlus}>+</span>
              <strong style={{ ...FUNNEL.panelHead, margin: 0 }}>{g.title}</strong>
            </div>
            <div style={{ ...panel, display: "flex", flexWrap: "wrap" }}>
              {g.tabs.map((t) => (
                <span key={t.label} data-store-tab={t.label} data-disabled={t.disabled ? "true" : "false"}
                  className={t.disabled ? "notAllowed" : undefined}
                  style={{ ...tab, flex: 1, textAlign: "center", color: t.disabled ? "var(--sg-grey-400)" : "var(--sg-ink-900)", cursor: t.disabled ? "not-allowed" : "pointer", borderBottom: "none" }}>
                  {t.label}
                </span>
              ))}
            </div>
          </section>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginTop: 24 }}>
          <a data-booking-continue href={onwardHref} style={cta}>Continue</a>
          <span style={FUNNEL.footnote}>Extras go on the same booking — you can review everything at the next step.</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── SCREEN 6 · REVIEW & PAY ─────────────────────────────── */

/**
 * Contract addendum §"SCREEN 6". Notable, all verified on live:
 *  • CUSTOMER INFO re-collects the passenger fields with **no `p1.` prefix** and COLLAPSES to
 *    `Edit (email)` once confirmed;
 *  • the order summary is per-LEG with a **ticket count** (children count as tickets);
 *  • there is **no promo field and no terms checkbox** at this step;
 *  • PAYMENT DETAILS is **inert structure only** — live mounts Stripe Elements here, and the real mount
 *    belongs to the payments sprint. Nothing here takes a card number and nothing can be paid.
 *  • TAX IS DISPLAY-ONLY (founder ruling): live's label is reproduced, no tax is computed, and the
 *    figure shown is the un-taxed cart total rather than an invented "with tax" number.
 */
export function BookingReviewScreen({ mountainCode, mountainName }: { mountainCode: string; mountainName: string }) {
  const items = useCartStore((s) => s.items);
  const [confirmed, setConfirmed] = useState(false);
  const [c, setC] = useState({ firstName: "", lastName: "", email: "", emailConfirm: "", phoneExt: "+1", phone: "" });
  const set = (k: keyof typeof c) => (e: React.ChangeEvent<HTMLInputElement>) => setC((s) => ({ ...s, [k]: e.target.value }));
  // S24 · F159/W10 — the CUSTOMER form pre-fills for a signed-in member too, kept SEPARATE from the
  // Passenger form (the two-form distinction is deliberate: traveller vs ticket-receiving customer).
  usePrefill(setC);

  const designLines = useDesignCartLines();
  const legs = useMemo(() => mergeDesignLegs(items, designLines, mountainCode), [items, designLines, mountainCode]);
  const total = legs.reduce((s, i) => s + (i.guestPrice ?? 0) * i.qty, 0);
  const ready = !!c.firstName.trim() && !!c.lastName.trim() && !!c.email.trim() && c.email.trim() === c.emailConfirm.trim() && !!c.phone.trim();

  // WO-COMMERCE-BIND-001 §14 · DoD 3 — SUBMIT TO THE LEGACY ENGINE.
  // This screen used to dead-end: "Payment is not connected in this build". It now hands the booking
  // to the engine that actually takes it — the same endpoint live's own funnel posts to. The route
  // behind this STAGES by default and only transmits when the founder arms it, because sending it
  // creates an order and emails a real bus operator (§10).
  const [submitState, setSubmitState] = useState<
    { phase: "idle" } | { phase: "sending" } | { phase: "staged"; detail: string } | { phase: "booked"; orderId: string } | { phase: "error"; detail: string }
  >({ phase: "idle" });

  /**
   * REVIEW & PAY — FOUNDER RULING 2026-09-26: there is no second checkout app to hand the browser to.
   * The card is taken on the funnel's own `/mountain-<code>/booking/review` (InpageCardPayment), which
   * creates the server-priced order itself, so this button only opens that page. It creates no order and
   * charges nothing.
   */
  const [payState, setPayState] = useState<{ phase: "idle" } | { phase: "creating" }>({ phase: "idle" });

  const reviewAndPay = () => {
    setPayState({ phase: "creating" });
    window.location.href = designCartCheckoutHref(mountainCode);
  };

  const submitToLegacy = async () => {
    setSubmitState({ phase: "sending" });
    try {
      const res = await fetch("/api/legacy/booking/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mountainCode,   // the server resolves this to live's numeric midentifier; see the route
          cart: legs.map((i) => ({
            scheduleIdentifier: i.identity ?? i.listingId,
            depart: isDepart(i),
            qty: i.qty,
            price: i.guestPrice ?? 0,
            supplier: i.supplier,
            startDate: i.startDate,
            depTime: i.depTime,
            depStop: i.depStop,
          })),
          cartAccount: {
            firstName: c.firstName.trim(), lastName: c.lastName.trim(),
            email: c.email.trim(), phone: c.phone.trim(), phoneExt: c.phoneExt,
          },
        }),
      });
      const json = (await res.json()) as LegacyBookingSubmitResponse;
      if (json.booked && json.orderId) {
        // SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — the second (and last) confirmed-payment branch in
        // this app. Same rail as @/lib/booking-pay-bridge's: the condition that is allowed to paint
        // "booked" is the condition that reports the sale, so the two can never disagree. Staged and
        // error below emit nothing. De-duplication is by order id inside `emitPurchaseOnce`, which
        // means a re-render or a re-submit of an already-booked order counts once.
        emitPurchaseOnce({
          transactionId: String(json.orderId),
          value: total,
          items: legs.map((i) => ({
            item_id: String(i.identity ?? i.listingId ?? i.key),
            item_name: i.title,
            quantity: i.qty,
            price: i.guestPrice ?? 0,
            item_brand: i.supplier,
            item_category: isDepart(i) ? "transport:depart" : "transport:return",
          })),
        });
        setSubmitState({ phase: "booked", orderId: String(json.orderId) });
      } else if (json.staged) setSubmitState({ phase: "staged", detail: json.reason ?? "staged" });
      else setSubmitState({ phase: "error", detail: json.error ?? `upstream ${json.upstreamStatus ?? res.status}` });
    } catch (e) {
      setSubmitState({ phase: "error", detail: e instanceof Error ? e.message : "network" });
    }
  };

  return (
    <div data-booking-screen="review">
      <FunnelChrome mountainCode={mountainCode} mountainName={mountainName} />
      <BookingStepper active="review" mountainCode={mountainCode} />
      <div style={{ ...wrap, display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 24 }}>
        <section style={{ flex: "1 1 320px", minWidth: 0 }}>
        <div data-review-substepper style={{ ...FUNNEL.panelHead, margin: "0 0 10px", color: "var(--sg-grey-500)" }}>
          TRANSPORT | MARKETPLACE | REVIEW &amp; PAYMENT
        </div>
        {/* The export's own review screen: title, then one line saying what happens on Pay. */}
        <h1 style={FUNNEL.h1}>Review &amp; pay</h1>
        <p style={FUNNEL.lede}>
          Cart items are not reserved. Nothing is charged now; submitting authorises your card, which is charged only
          if the booking is confirmed.
        </p>

        <section data-review-panel="customer" style={panel}>
          <header style={{ ...panelHead, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span>
              CUSTOMER INFO{" "}
              <span style={{ fontSize: 10, color: GREY }}>(YOU WILL RECEIVE YOUR TICKET VIA EMAIL)</span>
            </span>
            {confirmed ? (
              <button type="button" data-customer-edit onClick={() => setConfirmed(false)} style={{ ...linkish, color: ORANGE }}>
                Edit ({c.email})
              </button>
            ) : null}
          </header>
          {!confirmed ? (
            <div style={{ padding: 18 }}>
              <div style={grid2}>
                <input name="firstName" placeholder="First Name*" value={c.firstName} onChange={set("firstName")} style={input} />
                <input name="lastName" placeholder="Last Name*" value={c.lastName} onChange={set("lastName")} style={input} />
                <input name="email" placeholder="Email*" value={c.email} onChange={set("email")} style={input} />
                <input name="emailConfirm" placeholder="Email Confirmation*" value={c.emailConfirm} onChange={set("emailConfirm")} style={input} />
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <input name="phoneExt" value={c.phoneExt} onChange={set("phoneExt")} style={{ ...input, width: 70, flex: "0 0 70px" }} />
                <input name="phone" placeholder="Phone Number*" value={c.phone} onChange={set("phone")} style={{ ...input, flex: 1 }} />
              </div>
              <div style={{ marginTop: 16 }}>
                <button type="button" data-customer-continue disabled={!ready} onClick={() => setConfirmed(true)}
                  style={{ ...ctaOutline, ...(ready ? {} : { background: "var(--sg-grey-300)", cursor: "not-allowed" }) }}>
                  Continue
                </button>
              </div>
            </div>
          ) : null}
        </section>

        {/* payment appears only after CUSTOMER INFO is confirmed — live's own gating */}
        {confirmed ? (
          <section data-review-panel="payment" style={{ ...panel, marginTop: 20 }}>
            <header style={{ ...panelHead, display: "flex", justifyContent: "space-between" }}>
              <span>PAYMENT DETAILS</span>
              <span data-review-total data-tax="not-computed">
                Total (taxes &amp; fees included): <strong>{money(total)}</strong>
              </span>
            </header>
            <div style={{ padding: 18 }}>
              {/* INERT STRUCTURE. Live mounts Stripe Elements here; that mount belongs to the payments
                  sprint. These inputs are disabled and accept nothing — no card number can be typed.
                  DoD 10 note: the export's own CARD DETAILS step takes a live card number into a form
                  that sends nowhere. These stay disabled precisely so this screen cannot do that. */}
              <div data-payment-inert="true" style={grid3}>
                {[["Card number", "1234 1234 1234 1234"], ["Expiration date", "MM / YY"], ["Security code", "CVC"]].map(([l, ph]) => (
                  <label key={l} style={{ display: "block" }}>
                    <span style={FUNNEL.label}>{l}</span>
                    <input disabled placeholder={ph} className="notAllowed" style={{ ...input, background: "var(--sg-grey-50)", cursor: "not-allowed" }} />
                  </label>
                ))}
              </div>
              {/* WO-COMMERCE-BIND-001 §14 · DoD 3 — the card fields above stay inert: the CARD is
                  collected by the legacy engine's own gateway, which is where live collects it too.
                  What this screen now does is hand the BOOKING to that engine. */}
              <p style={{ ...FUNNEL.footnote, marginTop: 14 }}>
                Card details are entered on the next step, on our payment page. Your card is AUTHORIZED
                there and not charged: the amount is only taken once the booking is confirmed. If
                it is not, the authorization is released and you are never charged.
              </p>
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  data-review-checkout
                  disabled={payState.phase === "creating" || legs.length === 0}
                  onClick={reviewAndPay}
                  style={{ ...ctaOutline, ...(payState.phase === "creating" || legs.length === 0 ? { background: "var(--sg-grey-300)", cursor: "not-allowed" } : {}) }}
                >
                  {payState.phase === "creating" ? "Opening card details…" : "REVIEW & PAY"}
                </button>
                {submitState.phase === "booked" ? (
                  <p data-submit-result="booked" style={{ ...FUNNEL.notice, marginTop: 12 }}>
                    Booking received by the operator — order {submitState.orderId}.
                  </p>
                ) : null}
                {submitState.phase === "staged" ? (
                  <p data-submit-result="staged" style={{ ...FUNNEL.notice, marginTop: 12 }}>
                    No charge has been made. Your card is authorized when you place the booking. The operator
                    confirms your seats, then your card is charged and your confirmation is emailed to you. If
                    they cannot confirm, the authorization is released.
                  </p>
                ) : null}
                {submitState.phase === "error" ? (
                  <p data-submit-result="error" style={{ ...FUNNEL.notice, marginTop: 12, borderColor: "#c1121f", background: "#fdf0f0" }}>
                    The booking was not accepted: {submitState.detail}. Nothing was charged.
                  </p>
                ) : null}
              </div>
            </div>
          </section>
        ) : null}

        {/* The export renders each cart line as its own card, not as rows inside a table. */}
        <section data-review-panel="summary" style={{ marginTop: 20 }}>
          <header style={{ ...FUNNEL.panelHead, display: "flex", justifyContent: "space-between", margin: "0 0 10px" }}>
            <span>TRANSPORT</span>
            <span>Total: <strong style={{ color: NAVY }}>{money(total)}</strong></span>
          </header>
          {legs.length === 0 ? (
            <p style={{ ...panel, padding: 18, margin: 0, fontSize: 13, color: GREY }}>No transport selected.</p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
              {legs.map((i, li) => (
                <li key={i.id} data-review-leg={i.title} data-review-trip={i.tripId ?? ""} style={{ ...FUNNEL.line, flexWrap: "wrap" }}>
                  {/* FWLR-025-R7 · L — the first leg of each journey carries the journey's caption */}
                  {i.tripId && legs.findIndex((x) => x.tripId === i.tripId) === li ? (
                    <span data-review-journey={i.tripId} style={{ flex: "1 0 100%", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: ORANGE, marginBottom: 6 }}>
                      {journeyLabel({ id: i.id, qty: i.qty, price: i.guestPrice ?? 0, journey: i.journey, vendor: i.supplier })}
                    </span>
                  ) : null}
                  <span style={{ flex: "0 0 92px" }}>
                    <span style={{ ...FUNNEL.chip, borderColor: RULE }}>{isDepart(i) ? "DEPART" : "RETURN"}</span>
                    <span style={{ display: "block", marginTop: 4, fontSize: 11, color: GREY }}>{i.startDate}</span>
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.3, color: "var(--sg-ink-900)" }}>
                      {i.supplier}
                    </span>
                    {/* §6.3 — the board time-over-stop, carried from the results screen (F137). */}
                    {i.depTime || i.depStop ? (
                      <span style={{ display: "block", marginTop: 3, fontSize: 12.5, color: "var(--sg-grey-600)" }}>
                        {[i.depTime, i.depStop].filter(Boolean).join(" — ")}
                      </span>
                    ) : null}
                  </span>
                  {/* children count as tickets — the qty carried from the results screen */}
                  <span data-review-tickets style={{ fontFamily: "var(--sg-font-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--sg-grey-500)" }}>
                    {i.qty} Ticket{i.qty === 1 ? "" : "s"}
                  </span>
                  <span data-review-leg-subtotal style={{ fontSize: 14.5, fontWeight: 700, minWidth: 60, textAlign: "right", whiteSpace: "nowrap" }}>
                    {money((i.guestPrice ?? 0) * i.qty)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        </section>

        {/* THE EXPORT'S SUMMARY RAIL — its checkout keeps the total and the primary statement in a
            sticky right-hand column (`sideW` 340px, radius 14, glacier-200), and the funnel had no
            equivalent at all: the total was buried in a panel header. */}
        <aside data-review-rail style={{ ...FUNNEL.rail, position: "sticky", top: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ ...FUNNEL.label, marginBottom: 0, fontSize: 11 }}>Total</span>
            <strong style={{ ...FUNNEL.total, marginLeft: "auto" }}>{money(total)}</strong>
          </div>
          <ul style={{ margin: "16px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 9 }}>
            <li style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, color: "var(--sg-grey-600)" }}>
              <span>Tickets</span>
              <span style={{ color: "var(--sg-ink-900)" }}>{legs.reduce((s, i) => s + i.qty, 0)}</span>
            </li>
            <li style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, color: "var(--sg-grey-600)" }}>
              <span>Taxes &amp; fees</span>
              {/* live's own label, and it is NOT computed here — the founder ruling this screen carries. */}
              <span style={{ color: "var(--sg-ink-900)" }}>Included</span>
            </li>
          </ul>
        </aside>
      </div>
    </div>
  );
}

/* S23 · F146 — TRANSPORT EXTRAS. Three tabs, content sourced VERBATIM from the Figma booking-extras node
   (1811:161181): each carries body copy, a "Choose Direction" (Departure/Return), and a Non-Member / Member
   price (a quantity stepper for the two Checked tabs; a single-select for Trip Protection). Presentational
   here (the add-to-cart wiring for paid extras is a further step) — the founder's finding was that the tabs
   were EMPTY; this fills them with the real designed content. No copy is invented. */
const EXTRAS_TABS = [
  {
    key: "luggage",
    label: "Checked Luggage",
    kind: "stepper" as const,
    body: [
      "Each Ticket Holder May Bring One Standard Size Suitcase Or Duffel Bag No Larger Than 158 Centimeters (62 Inches) And Weighing A Maximum Of 23 Kilos (50 Pounds) Plus One Carry-On Bag That Can Fit In The Overhead Storage Compartment. Please Note That Overhead Storage Space On Buses Is Typically Smaller Than Found In Airplanes And Measures 23x 40.5x51 Cm (9x16x20 Inches). Bringing A Small Suitcase As A Carry-On Bag Weighing No More Than 12 Kg (25 Lbs) Is Perfectly Fine.",
      "Additional Luggage Will Be Free-Of-Charge And The Each Ticket Ticket Holder May Bring Up 5 Suitcase.",
    ],
    nonMember: 5.0,
    member: 0.0,
  },
  {
    key: "equipment",
    label: "Checked Equipment",
    kind: "stepper" as const,
    body: [
      "Each Ticket Holder May Also Bring One Set Of Skis Or Snowboard Or Golf Clubs. Additional Skis Or Snowboards Or Golf Clubs Will Be Free-Of-Charge.  Bikes Will Also Be Free-Of-Charge.",
    ],
    nonMember: 5.0,
    member: 0.0,
  },
  {
    key: "protection",
    label: "Trip Protection",
    kind: "select" as const,
    heading: "SNOWCLUB TRIP PROTECTION",
    refunds: ["Cash Refunds - up to 24hrs - Free", "Credit Refunds - up to 24hrs - Free", "Changes - up to 24hrs - Free"],
    body: [
      "All refunds & changes are processed online through My Tickets or your Member Account. This insurance is with snowclub and does not require contacting the transport operators.",
    ],
    nonMember: 50.0,
    member: 29.0,
  },
];

function TransportExtrasPanel() {
  const [activeKey, setActiveKey] = useState(EXTRAS_TABS[0]!.key);
  const [direction, setDirection] = useState<"departure" | "return">("departure");
  const [nonMemberQty, setNonMemberQty] = useState(2);
  const [memberQty, setMemberQty] = useState(2);
  const active = EXTRAS_TABS.find((t) => t.key === activeKey)!;
  // §25.6 — the second formatter existed only to force two decimals on the review summary. Same ruling.
  const money2 = (n: number) => `$${Math.round(n)}`;
  const stepper = (qty: number, set: (n: number) => void, price: number, memberSide: boolean) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12, color: "var(--sg-grey-600)" }}>Quantity:</span>
      <button type="button" onClick={() => set(Math.max(0, qty - 1))} style={extraStep}>−</button>
      <span style={{ minWidth: 16, textAlign: "center", fontSize: 13 }}>{qty}</span>
      <button type="button" onClick={() => set(Math.min(10, qty + 1))} style={extraStep}>+</button>
      <span style={{ fontSize: 13, fontWeight: 600, color: memberSide ? ORANGE : "var(--sg-ink-900)" }}>{money2(price)}</span>
    </div>
  );
  return (
    <section data-booking-panel="extras" style={{ ...panel, marginTop: 20 }}>
      <header style={{ ...panelHead, display: "flex", alignItems: "center", gap: 8 }}>
        <span aria-hidden style={chipPlus}>+</span> Transport Extras
      </header>
      <div style={{ display: "flex", borderBottom: `1px solid ${RULE}` }}>
        {EXTRAS_TABS.map((t) => (
          <button key={t.key} type="button" data-extras-tab={t.key} onClick={() => setActiveKey(t.key)}
            style={{ ...tab, color: activeKey === t.key ? ORANGE : "var(--sg-grey-600)", borderBottomColor: activeKey === t.key ? ORANGE : "transparent" }}>
            {t.label}
          </button>
        ))}
      </div>
      <div data-extras-content={active.key} style={{ padding: "18px 20px" }}>
        {active.kind === "select" ? (
          <div style={{ display: "flex", gap: 40, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ ...FUNNEL.panelHead, margin: "0 0 10px", color: "var(--sg-glacier-700)" }}>{active.heading}</div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Refunds &amp; Changes</div>
              {active.refunds!.map((r) => <div key={r} style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--sg-grey-600)" }}>• {r}</div>)}
            </div>
            <p style={{ flex: "1 1 300px", fontSize: 12, color: GREY, margin: 0 }}>{active.body[0]}</p>
          </div>
        ) : (
          <>{active.body.map((p) => <p key={p} style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--sg-grey-600)", margin: "0 0 10px" }}>{p}</p>)}</>
        )}
        <div style={{ display: "flex", gap: 40, flexWrap: "wrap", marginTop: 16, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Choose Direction</div>
            <div style={{ display: "flex", gap: 16, border: `1px solid ${RULE}`, borderRadius: 6, padding: "8px 12px" }}>
              {(["departure", "return"] as const).map((d) => (
                <label key={d} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={direction === d} onChange={() => setDirection(d)} style={{ accentColor: ORANGE }} />
                  {d === "departure" ? "Departure" : "Return"}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Non-Member</div>
            {active.kind === "stepper"
              ? stepper(nonMemberQty, setNonMemberQty, active.nonMember, false)
              : <label style={{ display: "flex", alignItems: "center", gap: 8, border: `1px solid ${RULE}`, borderRadius: 6, padding: "8px 14px" }}><input type="checkbox" style={{ accentColor: ORANGE }} /> {money2(active.nonMember)}</label>}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: ORANGE }}>Member</div>
            {active.kind === "stepper"
              ? stepper(memberQty, setMemberQty, active.member, true)
              : <label style={{ display: "flex", alignItems: "center", gap: 8, border: `1px solid ${ORANGE}`, borderRadius: 6, padding: "8px 14px", color: ORANGE }}><input type="checkbox" defaultChecked style={{ accentColor: ORANGE }} /> {money2(active.member)}</label>}
          </div>
        </div>
      </div>
    </section>
  );
}

/* S21 · F137 — the DETAILS "Ticket details" leg summary (contract §4). One row per transport line, each
   with its own Details expander (live opens per-leg detail here). Every value is read from the cart line
   the results screen created — nothing re-derived, nothing invented. */
function TicketDetailsPanel({ legs }: { legs: CartItem[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (legs.length === 0) return null;
  return (
    <section data-booking-panel="ticket-details" style={{ ...panel, marginTop: 20 }}>
      <header style={panelHead}>Ticket details</header>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {legs.map((i, li) => {
          const isOpen = open === i.id;
          // FWLR-025-R7 · L — the first leg of each journey carries the journey's caption
          const firstOfTrip = !!i.tripId && legs.findIndex((x) => x.tripId === i.tripId) === li;
          return (
            <li key={i.id} data-ticket-leg={i.title} data-ticket-trip={i.tripId ?? ""} style={{ borderTop: `1px solid ${RULE}`, padding: "14px 20px" }}>
              {firstOfTrip ? (
                <div data-ticket-journey={i.tripId} style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: ORANGE, marginBottom: 8 }}>
                  {journeyLabel({ id: i.id, qty: i.qty, price: i.guestPrice ?? 0, journey: i.journey, vendor: i.supplier })}
                </div>
              ) : null}
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <strong style={{ width: 92, flex: "0 0 92px", fontSize: 13 }}>
                  {isDepart(i) ? "DEPART" : "RETURN"}
                  <span style={{ display: "block", fontWeight: 400, fontSize: 11, color: GREY }}>{i.startDate}</span>
                </strong>
                <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--sg-ink-900)" }}>
                  <div style={{ fontWeight: 600 }}>{i.supplier}</div>
                  {i.depTime || i.depStop ? (
                    <div style={{ color: GREY, fontSize: 12 }}>{[i.depTime, i.depStop].filter(Boolean).join(" — ")}</div>
                  ) : null}
                </div>
                <span data-ticket-count style={{ fontSize: 13 }}>{i.qty} Ticket{i.qty === 1 ? "" : "s"}</span>
                <button type="button" data-ticket-details-toggle aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : i.id)}
                  style={{ ...linkish, color: ORANGE }}>
                  Details {isOpen ? "⌃" : "⌄"}
                </button>
                <span style={{ fontSize: 13, fontWeight: 600, minWidth: 54, textAlign: "right" }}>{money((i.guestPrice ?? 0) * i.qty)}</span>
              </div>
              {isOpen ? (
                <div data-ticket-detail style={{ marginTop: 8, fontSize: 12, color: GREY }}>
                  {i.qty} × {money(i.guestPrice ?? 0)}
                  {i.depStop ? ` · boards ${i.depStop}` : ""}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ── WO-COMMERCE-BIND-001 · DoD 10 — THE FUNNEL'S TOKENS ARE NOW THE EXPORT'S ─────────────────────
   Every constant below was live's ($eef5f7 panel heads, $2a9d8f teal links, 8px radii, Roboto 13px),
   which is exactly what the founder saw: "review and pay looks like a different non-new front end."
   They are now the export's own checkout values (see FUNNEL in BookingChrome, quoted with byte
   offsets from the design file). Presentation only — every `name`, `placeholder`, `data-*` hook and
   copy contract on these screens is unchanged, so the DoD 3a walk and behaviour gates still bind. */
const wrap: React.CSSProperties = FUNNEL.page;
const panel: React.CSSProperties = { ...FUNNEL.panel, padding: 0, overflow: "hidden" };
const panelHead: React.CSSProperties = {
  ...FUNNEL.panelHead,
  margin: 0,
  padding: "15px 18px",
  borderBottom: `1px solid ${RULE}`,
  background: "#fff",
};
const grid2: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 };
const grid3: React.CSSProperties = { display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 };
const input: React.CSSProperties = FUNNEL.input;
/** the export's quick-add tile (DETAILS' Luggage / Protect chips) */
const chip: React.CSSProperties = { position: "relative", border: `1px solid ${RULE}`, borderRadius: 11, padding: "12px 18px 20px", fontFamily: "var(--sg-font-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", background: "#fff" };
const chipPlus: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: "50%", background: ORANGE, color: "#fff", fontSize: 13, lineHeight: 1 };
const tab: React.CSSProperties = { flex: "0 0 auto", padding: "12px 18px", background: "none", border: "none", borderBottom: "2.5px solid transparent", fontFamily: "var(--sg-font-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" };
const extraStep: React.CSSProperties = { border: `1px solid ${RULE}`, background: "#fff", borderRadius: 6, width: 26, height: 26, cursor: "pointer", color: GREY, fontSize: 14, lineHeight: 1 };
// S24 · F157/W8 — the bottom funnel CTA is SOLID ORANGE (founder walk). DoD 10 keeps that ruling and
// re-derives its geometry from the export's own primary action (50px, radius 12, font-secondary
// 13/700 uppercase .09em) instead of live's 8px/Roboto pill.
const cta: React.CSSProperties = FUNNEL.cta;
const ctaOutline: React.CSSProperties = { ...FUNNEL.cta, border: "none" };
const linkish: React.CSSProperties = { border: "none", background: "none", fontFamily: "var(--sg-font-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", cursor: "pointer" };
